import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { grafikService } from '../services/grafikService';
import {
  formatHours, formatMonthYear, getMonthsList
} from '../utils/grafikHelpers';
import MyMonthView from '../components/grafik/MyMonthView';
import AvailabilityCalendar from '../components/grafik/AvailabilityCalendar';
import LeaveRequestModal from '../components/grafik/LeaveRequestModal';
import CalendarSubscribeCard from '../components/grafik/CalendarSubscribeCard';

const TABS = [
  { id: 'month', label: 'Moj miesiac', icon: 'fa-calendar-alt' },
  { id: 'availability', label: 'Dostepnosc', icon: 'fa-clock' }
  // Wnioski urlopowe tymczasowo wylaczone - tabela grafik_wnioski_urlopowe + API
  // pozostaja w systemie, mozna przywrocic dodajac wpis tutaj.
];

const STATUS_LEAVE_LABELS = {
  zlozony: { label: 'Zlozony', color: 'var(--info)' },
  zaakceptowany: { label: 'Zaakceptowany', color: 'var(--success)' },
  odrzucony: { label: 'Odrzucony', color: 'var(--danger)' }
};

const MySchedulePage = () => {
  const today = new Date();
  const [activeTab, setActiveTab] = useState('month');
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);

  const [shifts, setShifts] = useState([]);
  const [shiftsLoading, setShiftsLoading] = useState(false);
  const [summary, setSummary] = useState({});

  const [leaves, setLeaves] = useState([]);
  const [leavesLoading, setLeavesLoading] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);

  const yearsRange = [today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1];

  const loadMyMonth = useCallback(async () => {
    setShiftsLoading(true);
    try {
      const res = await grafikService.getMyMonth({ year, month });
      const data = res?.data || res;
      const arr = data?.shifts || data?.zmiany || (Array.isArray(data) ? data : []);
      setShifts(Array.isArray(arr) ? arr : []);
    } catch (err) {
      toast.error(err.message || 'Blad ladowania mojego grafiku');
    } finally {
      setShiftsLoading(false);
    }
  }, [year, month]);

  const loadMySummary = useCallback(async () => {
    try {
      const res = await grafikService.getMySummary({ year, month });
      const data = res?.data || res;
      setSummary(data?.summary || data || {});
    } catch (err) {
      console.warn('[MySchedule] Summary blad:', err.message);
    }
  }, [year, month]);

  const loadLeaves = useCallback(async () => {
    setLeavesLoading(true);
    try {
      const res = await grafikService.getMyLeaves();
      const arr = res?.data?.leaves || res?.data || [];
      setLeaves(Array.isArray(arr) ? arr : []);
    } catch (err) {
      toast.error(err.message || 'Blad ladowania wnioskow');
    } finally {
      setLeavesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'month') {
      loadMyMonth();
      loadMySummary();
    } else if (activeTab === 'leaves') {
      loadLeaves();
    }
  }, [activeTab, loadMyMonth, loadMySummary, loadLeaves]);

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={{
        marginBottom: '1rem',
        backgroundColor: 'var(--card-bg)',
        padding: '1.25rem 1.5rem',
        borderRadius: '6px',
        border: '1px solid var(--border)'
      }}>
        <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>
          <i className="fas fa-calendar-week" style={{ marginRight: '0.5rem' }} />
          Moj grafik
        </h2>
        <CalendarSubscribeCard />
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex', gap: '0.25rem',
        backgroundColor: 'var(--bg)',
        padding: '0.4rem',
        borderRadius: '6px',
        border: '1px solid var(--border)',
        marginBottom: '1rem'
      }}>
        {TABS.map(t => {
          const isActive = activeTab === t.id;
          return (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              style={{
                flex: 1,
                padding: '0.55rem 1rem',
                backgroundColor: isActive ? 'var(--info)' : 'transparent',
                color: isActive ? '#fff' : 'var(--text-secondary)',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: 600
              }}>
              <i className={`fas ${t.icon}`} style={{ marginRight: '0.4rem' }} />
              {t.label}
            </button>
          );
        })}
      </div>

      <div style={{
        backgroundColor: 'var(--card-bg)',
        borderRadius: '6px',
        border: '1px solid var(--border)',
        padding: '1.25rem'
      }}>
        {activeTab === 'month' && (
          <div>
            {/* Selector miesiaca */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              marginBottom: '1rem', padding: '0.75rem',
              backgroundColor: 'var(--bg)', borderRadius: '4px',
              border: '1px solid var(--border)'
            }}>
              <button onClick={() => {
                if (month === 1) { setMonth(12); setYear(y => y - 1); }
                else setMonth(m => m - 1);
              }}
                style={{
                  padding: '0.4rem 0.7rem',
                  backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)',
                  color: 'var(--text)', borderRadius: '4px', cursor: 'pointer'
                }}>&laquo;</button>
              <select value={month} onChange={(e) => setMonth(Number(e.target.value))}
                style={{
                  padding: '0.4rem 0.5rem', backgroundColor: 'var(--card-bg)',
                  border: '1px solid var(--border)', borderRadius: '4px', color: 'var(--text)'
                }}>
                {getMonthsList().map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
              <select value={year} onChange={(e) => setYear(Number(e.target.value))}
                style={{
                  padding: '0.4rem 0.5rem', backgroundColor: 'var(--card-bg)',
                  border: '1px solid var(--border)', borderRadius: '4px', color: 'var(--text)'
                }}>
                {yearsRange.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <button onClick={() => {
                if (month === 12) { setMonth(1); setYear(y => y + 1); }
                else setMonth(m => m + 1);
              }}
                style={{
                  padding: '0.4rem 0.7rem',
                  backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)',
                  color: 'var(--text)', borderRadius: '4px', cursor: 'pointer'
                }}>&raquo;</button>
              <span style={{ marginLeft: '0.5rem', fontWeight: 600, color: 'var(--text)' }}>
                {formatMonthYear(year, month)}
              </span>
            </div>

            {/* Card sumy godzin */}
            <div style={{
              padding: '0.85rem 1rem',
              backgroundColor: 'rgba(13,110,253,0.08)',
              border: '1px solid var(--info)',
              borderRadius: '6px',
              marginBottom: '1rem',
              display: 'flex', flexWrap: 'wrap', gap: '1.5rem',
              fontSize: '0.85rem'
            }}>
              <div>
                <span style={{ color: 'var(--text-secondary)' }}>Planowane:</span>{' '}
                <strong>{formatHours(summary.planowane || summary.suma_planowane || summary.total_hours || 0)}</strong>
              </div>
              <div>
                <span style={{ color: 'var(--text-secondary)' }}>Zaakceptowane:</span>{' '}
                <strong style={{ color: 'var(--success)' }}>{formatHours(summary.zaakceptowane || summary.zatwierdzone || 0)}</strong>
              </div>
              {/* Urlop/Chorobowe ukryte - wnioski urlopowe wylaczone */}
            </div>

            {shiftsLoading ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                Ladowanie...
              </div>
            ) : (
              <MyMonthView
                year={year} month={month} shifts={shifts}
                onAccepted={() => { loadMyMonth(); loadMySummary(); }}
              />
            )}
          </div>
        )}

        {activeTab === 'availability' && (
          <AvailabilityCalendar />
        )}

        {activeTab === 'leaves' && (
          <div>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: '1rem'
            }}>
              <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600 }}>
                Moje wnioski urlopowe ({leaves.length})
              </h4>
              <button onClick={() => setShowLeaveModal(true)}
                style={{
                  padding: '0.5rem 1.25rem',
                  backgroundColor: 'var(--info)', color: '#fff',
                  border: 'none', borderRadius: '4px', cursor: 'pointer',
                  fontSize: '0.85rem', fontWeight: 600
                }}>
                <i className="fas fa-plus" style={{ marginRight: '0.4rem' }} />
                Zloz nowy
              </button>
            </div>
            {leavesLoading && (
              <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                Ladowanie...
              </div>
            )}
            {!leavesLoading && leaves.length === 0 && (
              <div style={{
                padding: '2rem', textAlign: 'center', color: 'var(--text-muted)',
                backgroundColor: 'var(--bg)', borderRadius: '4px'
              }}>
                Brak wnioskow urlopowych
              </div>
            )}
            {leaves.length > 0 && (
              <div style={{
                border: '1px solid var(--border)', borderRadius: '6px', overflow: 'hidden'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--bg)' }}>
                      <th style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 600 }}>Od</th>
                      <th style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 600 }}>Do</th>
                      <th style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 600 }}>Typ</th>
                      <th style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 600 }}>Status</th>
                      <th style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 600 }}>Notatka</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaves.map(l => {
                      const st = STATUS_LEAVE_LABELS[l.status] || { label: l.status, color: 'var(--text-muted)' };
                      return (
                        <tr key={l.id} style={{ borderTop: '1px solid var(--border)' }}>
                          <td style={{ padding: '0.5rem 0.75rem' }}>{l.data_od}</td>
                          <td style={{ padding: '0.5rem 0.75rem' }}>{l.data_do}</td>
                          <td style={{ padding: '0.5rem 0.75rem' }}>{l.typ}</td>
                          <td style={{ padding: '0.5rem 0.75rem' }}>
                            <span style={{
                              padding: '0.15rem 0.5rem',
                              backgroundColor: `${st.color}22`,
                              color: st.color,
                              borderRadius: '3px',
                              fontSize: '0.75rem', fontWeight: 600
                            }}>{st.label}</span>
                          </td>
                          <td style={{ padding: '0.5rem 0.75rem', color: 'var(--text-muted)' }}>
                            {l.notatka_pracownika || '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {showLeaveModal && (
        <LeaveRequestModal
          onClose={() => setShowLeaveModal(false)}
          onSubmitted={() => { loadLeaves(); }}
        />
      )}
    </div>
  );
};

export default MySchedulePage;
